import { Errors } from '@equifinance/shared';
import { PropertyType } from '../account';
import { Stages } from '../../types/Stages';

export enum Status {
  AwaitingDecision = 'AWAITING DECISION',
  Approved = 'APPROVED',
  Completed = 'COMPLETED',
  Reject = 'REJECT',
  Rejected = 'REJECTED',
  Overridden = 'OVERRIDDEN',
  AutoApproved = 'AUTO-APPROVED',
  Override = 'OVERRIDE',
  Offered = 'OFFERED',
  ApprovedWithException = 'APPROVED WITH EXCEPTION',
}

export enum ChecklistIndicatorType {
  AttentionNeeded = 'AttentionNeeded',
  Complete = 'Complete',
  Locked = 'Locked',
}

export enum PostOfferDecision {
  DocumentsReturnedComplete = 'Documents returned - complete',
  DocumentsReturnedUnableToComplete = 'Documents returned - unable to complete',
}

export enum ProductDecision {
  AutoApproved = 'Auto-approved',
  Override = 'Override',
}

export enum NoteType {
  Call = 'Call',
  Email = 'Email',
  SystemNote = 'SystemNote',
  General = 'General',
}

export type Note = {
  id: string,
  noteFromUserId: string,
  noteFromUserName: string,
  note: string,
  type: keyof typeof NoteType,
  moment: string,
}

export type ChecklistStages = Stages.AtLender | Stages.Offer | Stages.Completion

export enum ChecklistDocumentStatus {
  Uploaded = 'Uploaded',
  Offered = 'Offered',
  Cancelled = 'Cancelled'
}

export type ChecklistDocument = {
  id: string,
  documentId: string,
  status: keyof typeof ChecklistDocumentStatus,
  fileName: string,
  uploadedDate: string,
  expiryDate?: string,
}

export type ChecklistDocumentPayload = {
  fileName: string,
  documentData: string,
  documentType: string,
}

export type UploadChecklistDocumentPayload = {
  checklistSectionId: string,
  documents: ChecklistDocumentPayload[],
}

export interface ChecklistItem {
  version: number,
  id: string,
  status: keyof typeof Status,
  stage: ChecklistStages,
  isLocked: boolean,
  checklistIndicatorType: keyof typeof ChecklistIndicatorType,
  canSubmit: boolean,
  submitted: boolean,
  submittedById: string,
  submittedByName: string,
  submittedTimestamp: string,
  notes: Note[],
}

export enum SecurityCallStatus {
  Outstanding = 'Outstanding',
  CompleteAccepted = 'Complete - Accepted',
  CompleteFailed = 'Complete - Failed',
}

export type SecurityCall = {
  id: string,
  applicantId: string,
  applicantOrdinal: number,
  status: keyof typeof SecurityCallStatus,
  statusLastChangedDate: string,
}

export type SendOfferTitleDetails = {
  id: string,
  applicantNames: string,
  securityAddress: string,
  titleNumbers: { id?: string, titleNumber: string }[],
}

export enum OfferType {
  Esig = 'Esig',
  Postal = 'Postal',
}

export enum VerificationType {
  EQUIFAX_PASSED = 'Equifax E-ID Passed',
  MANUAL_VERIFICATION_COMPLETED = 'Manual ID Verification Completed',
}

export enum SendOfferDetailsStatus {
  AwaitingApproval = 'Awaiting Approval',
  Created = 'Created',
  CreationFailed = 'Creation Failed',
  CreationPending = 'Creation Pending',
  InProgress = 'In Progress',
  Complete = 'Complete',
  Expired = 'Expired',
  Cancelled = 'Cancelled',
  AuthenticationFailed = 'Authentication Failed',
}

export type SendOfferDetails = {
  id: string,
  offerType: keyof typeof OfferType | null,
  status: keyof typeof SendOfferDetailsStatus,
  createdDate: string,
  expiryDate: string,
  completedDate: string,
  sessionId: string,
  canChangeType: boolean,
}

export enum SendOfferDecision {
  Offered = 'Offered'
}

export interface SendOffer extends ChecklistItem {
  securityCall: SecurityCall[],
  titleDetails: SendOfferTitleDetails,
  possibleOfferTypes: (keyof typeof OfferType)[],
  offer: SendOfferDetails,
  documents: ChecklistDocument[],
  decision: keyof typeof SendOfferDecision,
  possibleDecisions: (keyof typeof SendOfferDecision)[],
}

export type SendOfferPayload = {
  typeOfSignature: keyof typeof OfferType,
  verificationTypes?: string[]
}

export enum OS1OS3Decision {
  AwaitingDecision = 'AwaitingDecision',
  Completed = 'Completed',
}

export enum OS1OS3PriorityRegisteredType {
  OS1 = 'OS1',
  OS3 = 'OS3',
}

export type OS1OS3Title = {
  id: string,
  titleNumber: string,
  equitableCharge: boolean,
  daylist: boolean,
  priorityRegistered: keyof typeof OS1OS3PriorityRegisteredType,
  lastRegistered: string,
  expiryDate: string,
  manualTaskAdded: boolean,
}

export interface OS1OS3 extends ChecklistItem {
  securityAddress: string,
  decision: keyof typeof OS1OS3Decision,
  possibleDecisions: (keyof typeof OS1OS3Decision)[],
  titles: OS1OS3Title[];
}

export type Action = {
  id: string,
  title: string,
  checked: boolean,
}

export type ApplicantType =
  'TODO';

export type ComplianceDecision =
  'TODO';

export type Title =
  'TODO';

export type ActingSolicitors =
  'TODO';

export enum ProductTypes {
  Fixed = 'Fixed',
  Variable = 'Variable',
}

export type SelectedProduct = {
  id: string,
  productId: string,
  type: keyof typeof ProductTypes,
  productCode: string,
  initialRate: string,
  initialPayment: number,
}

export enum ValuationType {
  Avm = 'AVM',
  DriveBy = 'Drive By',
  Full = 'Full Valuation',
}

export enum AvmResultType {
  InitialSearch = 'Initial Search',
  FullSearch = 'Full Search',
}

export type AvmSearchTypes = keyof typeof AvmResultType;

export enum ConfidenceBandType {
  High = 'High',
  Medium = 'Medium',
  Low = 'Low',
}

export type ValuationResult = {
  valuationType: keyof typeof ValuationType,
  valuersName: string,
  valuationAmout: number,
  valuationDate: string,
  confidence: number,
}

export type AvmInputData = {
  effectiveDate: string,
  propertyType: keyof typeof PropertyType,
  isStandardConstruction: boolean,
  isExCouncil: boolean,
  bedrooms: number,
  livingRooms: number,
  parking: boolean,
}

export type AvmOutcomeData = {
  valuerName: string,
  valuerReferenceNumber: string,
  valuationAmount: number,
  valuationConfidenceBand: keyof typeof ConfidenceBandType,
  valuationConfidenceValue: number,
  propertyType: keyof typeof PropertyType,
  isStandardConstruction: boolean,
  isExCouncil: boolean,
  bedrooms: number,
  livingRooms: number,
  parking: boolean,
}

export type AvmResult = {
  id: string,
  triggeredByUserId: string,
  triggeredByUserName: string,
  type: AvmSearchTypes,
  valuationDate: string,
  inputs: AvmInputData,
  outcome: AvmOutcomeData,
}

export enum ValuationDecision {
  Approved = 'Approved',
  ApprovedWithException = 'Approved with exception',
  Rejected = 'Rejected',
}

export interface Valuation extends ChecklistItem {
  justification?: string,
  valuationResult: ValuationResult
  avmResult: AvmResult[],
  decision: keyof typeof ValuationDecision,
  possibleDecisions: (keyof typeof ValuationDecision)[],
  documents: ChecklistDocument[],
}

export interface PostOffer extends ChecklistItem {
  decisionDetails: string,
  actions: Action[],
  dates: {
    id: string,
    completionDate: string,
    collectionDate: string,
    firstPaymentAmount: number,
    dueDate: {
      id: string,
      specificDayOfMonth: number,
      isLastWorkingDayOfTheMonth: boolean,
    }
  },
  documents: ChecklistDocument[],
  decision: keyof typeof PostOfferDecision,
  possibleDecisions: (keyof typeof PostOfferDecision)[],
}

export interface FraudChecks extends ChecklistItem {
  canBeSubmitted: boolean,
  applicants: {
    id: string,
    applicantType: ApplicantType,
    aml: {
      id: string,
      residencyVerification: {
        registeredOnElectoralRoll: boolean,
        numberOfDifferentContributingCreditInformation: number,
        listedOnTelephoneDatabase: boolean,
        courtAndInsolvencyInformation: string
      },
      identityVerification: {
        telephoneNumberMatch: string,
        electoralRollDobMatch: string,
        creditInformationDobMatch: string
      },
      alertIndicators: {
        sanctions: string,
        pep: string,
        relativeAndCloseAssociates: string,
        haloDeceased: string,
        CIFASListings: string
      }
    },
    watchlist: {
      id: string,
      complianceDecision: ComplianceDecision,
      justification: string,
      authorisedById: string,
      authorisedByName: string,
      submitted: boolean,
      decisionTimestamp: string
    },
    CIFAS: {
      id: string,
      complianceDecision: ComplianceDecision,
      justification: string,
      authorisedById: string,
      authorisedByName: string,
      submitted: boolean,
      decisionTimestamp: string
    }
  }[]
}

export interface Income extends ChecklistItem {
  applicants: {
    id: string,
    title: Title,
    forename: string,
    surname: string,
    declaredNetMonthlyIncome: number,
    overriddenNetMonthlyIncome: number
  }[]
}

export interface Product extends ChecklistItem {
  underwriterSelectedProduct: SelectedProduct,
  brokerSelectedProduct: SelectedProduct,
  overrideNotes: Note[],
  justification: string,
  authorisedById: string,
  authorisedByName: string,
  decision: keyof typeof ProductDecision | null,
  possibleDecisions: (keyof typeof ProductDecision | null)[],
}

export enum PolicyShieldsDecision {
  AutoApproved = 'Auto-approved',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

export enum PolicyShieldsRuleDecisionType {
  Approved = 'Approved',
  Rejected = 'Rejected',
}

export enum PolicyShieldsRuleOutcomeType {
  Referral = 'Referral',
  Rejection = 'Rejection'
}

export interface PolicyShieldsRuleOutcome {
  outcome: PolicyShieldsRuleOutcomeType,
  title: string,
  description: string,
  decisionById: string,
  decisionByName: string,
  decisionTimestamp: string,
  id: string,
  key: string,
  decision: PolicyShieldsRuleDecisionType,
  justification: string,
}

export interface PolicyShieldsRuleDecision {
  key: string,
  decision: PolicyShieldsRuleDecisionType,
  decisionById: string,
  decisionByName: string,
  decisionTimestamp: string,
  justification: string,
}

export interface PolicyShields extends ChecklistItem {
  possibleDecisions: (keyof typeof PolicyShieldsDecision)[],
  decision: keyof typeof PolicyShieldsDecision,
  justification: string,
  authorisedById: string,
  authorisedByName: string,
  ruleOutcomes: PolicyShieldsRuleOutcome[],
  ruleDecision: PolicyShieldsRuleDecision[],
}

export interface LandRegistry extends ChecklistItem {
  titles: {
    id: string,
    equitableCharge: boolean,
    anythingElseRegistered: boolean,
    registeredPriority: boolean,
    registryDate: string,
    expiryDate: string
  }[]
}

export interface ReportOnTitle extends ChecklistItem {
  reportRequestDate: string,
  reportReceivedDate: string,
  actingSolicitors: ActingSolicitors,
  solicitorCondition: {
    condition: string,
    taskAdded: boolean,
    satisfied: boolean
  },
  additionalSolicitorConditions: {
    id: string,
    condition: {},
    taskAdded: string,
    satisfied: boolean
  }[]
}

export type BaseChecklist = {
  updateCount: number,
  dirtyCount: number,

  errors: Errors,
  warnings: Errors,
  stage: Stages,

  id: string,
  postOffer: PostOffer | null,
  securityCall: ChecklistItem | null,
  sendOffer: SendOffer | null,
  fraudChecks: FraudChecks | null,
  bankruptcyChecks: ChecklistItem | null,
  income: Income | null,
  electoralRoll: ChecklistItem | null,
  product: Product,
  policyShields: PolicyShields | null,
  landRegistry: LandRegistry | null,
  reportOnTitle: ReportOnTitle | null,
  additionalSupport: ChecklistItem | null,
  valuation: Valuation | null,
  os1os3: OS1OS3 | null,
}

type OmitProperties<T, K extends keyof ChecklistItem> = T extends ChecklistItem ? Omit<T, K> : T;

type PropertiesToOmit = keyof Omit<ChecklistItem, 'id' | 'decision' | 'submitted'>;

type SelectedProductRequest = Pick<SelectedProduct, 'id' | 'productCode'>

interface ProductRequest extends Omit<Product, 'brokerSelectedProduct' | 'underwriterSelectedProduct'> {
  underwriterSelectedProduct: SelectedProductRequest
}

export type ChecklistRequest = Omit<{
  [K in keyof BaseChecklist]: K extends 'product'
  ? ProductRequest
  : OmitProperties<BaseChecklist[K], PropertiesToOmit>
}, 'errors' | 'warnings' | 'updateCount' | 'dirtyCount'>

type ChecklistResponse = BaseChecklist

export type Checklist = ChecklistResponse

export type ChecklistNotePayload = {
  checklistSectionId: string;
  note: string;
}

export type ChecklistOverrideProductPayload = {
  action: 'start' | 'submit' | 'cancel' | 'remove';
  productId: string | null;
}
